/**
 * Bundle 108 - Response detail modal reused by both the
 * Respondents view (Bundle 105) and the Survey Console Responses
 * tab. Renders every answer paired with its question label, masks
 * PII by default, unmask on super_admin re-confirm, and shows
 * drift + migration + audit-only badges.
 *
 * Data source: GET /api/admin/events/{eventId}/survey/response/{id}
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_BADGE = {
  submitted: 'bg-success-50 text-success-700 border-success-200',
  started: 'bg-blue-50 text-blue-800 border-blue-200',
  empty: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  pending: 'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const _driftLabel = (reason) => {
  if (!reason) return null;
  if (reason.startsWith('response_discovery_key_pre_bundle_89:')) {
    return `Pre-Bundle-89 discovery key (${reason.split(':', 2)[1]}).`;
  }
  if (reason.startsWith('response_future_event_key_pre_bundle_89:')) {
    return `Pre-Bundle-89 future-event key (${reason.split(':', 2)[1]}).`;
  }
  if (reason === 'response_future_event_was_numeric_rating') {
    return 'Numeric rating for future_event_likelihood - pre-Bundle-89 renderer.';
  }
  if (reason.startsWith('response_connection_intent_pre_bundle_89:')) {
    return `Pre-Bundle-89 connection_intent value (${reason.split(':', 2)[1]}).`;
  }
  if (reason.startsWith('response_testimonial_permission_pre_bundle_89:')) {
    return `Pre-Bundle-89 testimonial_permission value (${reason.split(':', 2)[1]}).`;
  }
  return reason;
};

export const ResponseDetailModal = ({ eventId, responseId, open, onOpenChange }) => {
  const [detail, setDetail] = useState(null);
  const [unmasked, setUnmasked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !responseId) return;
    setLoading(true); setDetail(null); setError(null); setUnmasked(false);
    api.get(`/admin/events/${eventId}/survey/response/${responseId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setError('Could not load this response.'))
      .finally(() => setLoading(false));
  }, [open, responseId, eventId]);

  const requestUnmask = async () => {
    if (!window.confirm(
      'Reveal PII (email, phone, contacts) for this response? '
      + 'This action is audited.'
    )) return;
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/response/${responseId}`,
        { params: { unmask: 1 } },
      );
      setDetail(data); setUnmasked(true);
    } catch (e) {
      toast.error('Could not unmask this response.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                      data-testid="console-response-detail-dialog">
        <DialogHeader>
          <DialogTitle>Response detail</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-4 text-center text-neutral-500">Loading...</div>
        ) : error ? (
          <div className="p-4 text-center text-red-800"
                data-testid="console-response-detail-error">{error}</div>
        ) : detail ? (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`${STATUS_BADGE[detail.status] || STATUS_BADGE.pending} border`}>
                {detail.status}
              </Badge>
              {detail.is_test ? (
                <Badge className="bg-yellow-50 text-yellow-800 border-yellow-200 border">
                  test
                </Badge>
              ) : null}
              {detail.is_migrated_from_pre_bundle89 ? (
                <Badge className="bg-red-50 text-red-800 border-red-200 border"
                        data-testid="console-detail-migrated-badge">
                  pre-Bundle-89 migrated
                </Badge>
              ) : null}
              {detail.audit_only ? (
                <Badge className="bg-neutral-100 text-neutral-700 border-neutral-200 border"
                        data-testid="console-detail-audit-only-badge">
                  audit only
                </Badge>
              ) : null}
              {detail.submitted_at ? (
                <span className="text-xs text-neutral-500 ml-auto">
                  Submitted {new Date(detail.submitted_at).toLocaleString()}
                </span>
              ) : null}
            </div>
            {detail.drift_reasons?.length ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 items-start"
                    data-testid="console-detail-drift-banner">
                <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5" />
                <div>
                  <div className="text-red-800 font-semibold text-xs uppercase tracking-widest">
                    Response carries pre-Bundle-89 values
                  </div>
                  <ul className="text-xs text-red-800 list-disc ml-4 mt-1">
                    {detail.drift_reasons.map((r) => (
                      <li key={r}>{_driftLabel(r)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <div className="text-xs text-neutral-500">
                {unmasked
                  ? 'PII is currently VISIBLE. Refresh to remask.'
                  : 'Contact fields are masked. Unmask requires re-confirm.'}
              </div>
              {!unmasked ? (
                <Button size="sm" variant="outline"
                          onClick={requestUnmask}
                          data-testid="console-detail-unmask-btn">
                  <Eye className="h-3 w-3 mr-1" /> Unmask PII
                </Button>
              ) : (
                <Button size="sm" variant="outline"
                          onClick={() => { setUnmasked(false); setDetail((d) => ({ ...d })); }}
                          data-testid="console-detail-remask-btn">
                  <EyeOff className="h-3 w-3 mr-1" /> Re-mask
                </Button>
              )}
            </div>
            <div className="border border-neutral-200 rounded-md divide-y"
                  data-testid="console-detail-answers">
              {(detail.answers || []).length === 0 ? (
                <div className="p-3 text-neutral-500 text-xs">
                  No answers on this response yet.
                </div>
              ) : (detail.answers || []).map((a) => (
                <div key={a.key} className="p-3"
                      data-testid={`console-detail-answer-${a.key}`}>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">
                    {a.label}
                  </div>
                  <div className={`mt-1 text-sm break-words ${a.drift_reason ? 'text-red-800' : 'text-primary-900'}`}>
                    {a.is_pii && !unmasked
                      ? String(a.masked_value ?? '')
                      : (typeof a.value === 'object'
                          ? JSON.stringify(a.value)
                          : String(a.value ?? ''))}
                    {a.is_pii ? (
                      <span className="ml-2 text-xs text-neutral-400">[PII]</span>
                    ) : null}
                    {a.drift_reason ? (
                      <span className="ml-2 text-xs text-red-700">[drift]</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {detail.invitation_id ? (
              <div className="text-xs text-neutral-500 border-t border-neutral-100 pt-2"
                    data-testid="console-detail-invitation-link">
                Invitation id: <code className="font-mono">{detail.invitation_id}</code>
              </div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ResponseDetailModal;
